import {CBARDynamicObject} from "./CBARDynamicObject";
import {CBARPipeline} from "./CBARPipeline";
import * as THREE from "three";
import {CBARMaterialProperty, CBARPaintMaterial} from "../../components";
import {CBARFrame} from "../CBARFrame";
import {Mat, Point, Rect} from "mirada";
import {canvasCreate, parseIncludes} from "../Utils";
import {CBARMaskedMaterial} from "../../components/CBARMaskedMaterial";

const NO_MASK = true;
const USE_SPRITE = false;

export class CBARDynamicWall extends CBARDynamicObject {

    constructor(pipeline:CBARPipeline, seedPoint:Point, width:number, height:number) {
        super(pipeline, seedPoint);

        this.maskCanvas = canvasCreate({width:1024, height:1024});

        this.maskTexture = new THREE.Texture(this.maskCanvas);

        const shape = new THREE.Shape();
        const points:THREE.Vector2[] = [
            new THREE.Vector2(-width/2, -height/2),
            new THREE.Vector2(width/2, -height/2),
            new THREE.Vector2(width/2, height/2),
            new THREE.Vector2(-width/2, height/2),
        ];
        shape.setFromPoints(points);

        const geometry = new THREE.ShapeBufferGeometry(shape);
        const background = this.context.gl.scene.background as THREE.Texture;

        if (NO_MASK) {
            //this.material = new CBARPaintMaterial(pipeline.context);
            if (USE_SPRITE) {
                const material = new THREE.SpriteMaterial( { map:background } );
                //material.alphaMap = this.maskTexture;
                material.onBeforeCompile = (shader) => {
                    this.onBeforeCompileCallback(shader)
                };
                const sprite = new THREE.Sprite(material);
                sprite.position.set(0,0,0);
                sprite.scale.set(5,5,1);
                this.model = sprite;
            } else {
                this.material = new CBARMaskedMaterial(pipeline.context, this.maskTexture);
                this.model = new THREE.Mesh(geometry, this.material.threeMaterial);
            }
        } else {
            this.material = new CBARPaintMaterial(pipeline.context);

            this.material.threeMaterial.alphaMap = this.maskTexture;
            this.material.setMaterialProperty(CBARMaterialProperty.color, this.context.color.cssColor);
            this.material.threeMaterial.depthTest = false;
            this.material.threeMaterial.clipShadows = true;
            this.material.threeMaterial.depthWrite = false;

            this.material.threeMaterial.lightMap = background;

            this.maskTexture.needsUpdate = true;
            this.material.threeMaterial.needsUpdate = true;

            this.model = new THREE.Mesh(geometry, this.material.threeMaterial);
        }

        this.setRenderObject(this.model);
    }

    protected onBeforeCompileCallback(shader:THREE.Shader) {

        shader.fragmentShader = parseIncludes(shader.fragmentShader);

        //todo: consider changing to just an import of a separate file
        //ADD YOUR FUNCTIONS HERE:

        shader.fragmentShader = shader.fragmentShader.replace("gl_FragColor = linearToOutputTexel( gl_FragColor );",
            "gl_FragColor.rgb = diffuseColor.rgb;");

        //console.log(shader.fragmentShader);
    }

    protected model:THREE.Mesh|THREE.Sprite;
    public material?:CBARPaintMaterial|CBARMaskedMaterial;

    public readonly maskTexture:THREE.Texture;
    public readonly maskCanvas:HTMLCanvasElement;

    public maskOrigin?:Point;
    public maskFrameIndex?:number;
    public maskCrop?:Rect;

    getMask() {
        const mask = cv.imread(this.maskCanvas);
        cv.cvtColor(mask, mask, cv.COLOR_RGBA2GRAY, 0);
        return mask;
    }

    setMask(mask:Mat, maskOrigin:Point, crop:Rect, frameIndex:number) {
        if (!this.material) return;

        this.maskCanvas.width = mask.cols;
        this.maskCanvas.height = mask.rows;
        cv.imshow(this.maskCanvas, mask);

        this.maskOrigin = maskOrigin;
        this.maskFrameIndex = frameIndex;
        this.maskCrop = crop;
        this.maskTexture.needsUpdate = true;

        this.material.threeMaterial.needsUpdate = true;
    }

    public lightingFrameIndex?:number;

    setLightingFactors(alpha:number, offset:number, frameIndex:number) {
        if (!this.material) return;

        this.material.lightingFactor = alpha;
        this.material.lightingOffset = offset;
        this.lightingFrameIndex = frameIndex;
    }

    public refresh() {
        this.material?.setMaterialProperty(CBARMaterialProperty.color, this.context.color.cssColor);
    }

    private update2dPosition() {
        if (!this.material || !this.maskOrigin || !this.maskCrop) return;

        this.material.uniforms["u_viewportSize"] = {type:"vec2", value:this.context.gl.resolution}

        const scale = this.maskCanvas.width / this.pipeline.inputSize.width;
        const maskOffsetX = scale * (this.maskOrigin.x - this.seedPoint.x);
        const maskOffsetY = scale * (this.maskOrigin.y - this.seedPoint.y);

        //0-1 normalized crop
        const cropX = (this.maskCrop.x + maskOffsetX) / this.maskCanvas.width;
        const cropY = (this.maskCrop.y + maskOffsetY) / this.maskCanvas.height;
        const cropWidth = this.maskCrop.width / this.maskCanvas.width;
        const cropHeight = this.maskCrop.height / this.maskCanvas.height;

        //use gl coordinate space (y flipped)
        this.material.maskCrop = new THREE.Vector4(cropX, 1 - cropHeight - cropY, cropWidth, cropHeight);
    }

    update(frame:CBARFrame) {
        this.update2dPosition();
        //set paint color each frame
        this.material?.setMaterialProperty(CBARMaterialProperty.color, this.context.color.cssColor);

        super.update(frame);
    }
}
